import React, { useEffect, useState } from 'react';
import Table from '../../../../theme/ui/Atoms/Table/Table';
import Button from '../../../../theme/ui/Atoms/Button';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AirConnectIcon,
  InfoIcon,
  ScanAirConnectIcon,
} from '../../../../assets';
import { useTranslation } from 'react-i18next';
import PaperButton from '../../../../theme/ui/Atoms/PaperButton';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useBack from '../../../../shared/util/route-dom/hooks/useBack';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../BackButton';
import CommanderAirConnectAPI from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI';
import useParseErrorMessage from '../../../../shared/hooks/useParseErrorMessage';
import { waitPromise } from '../../../../shared/util/waitPromise';
import { GetWifiSignalIcon } from '../../../../shared/util/rssi/getIcon';

// one array of at least one string
const validationSchema = yup.array().of(yup.string()).min(1);

const initialValues: string[] = [];
function ScanAirConnect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const parseErrorMessage = useParseErrorMessage();
  const { commanderId = '' } = useParams<{
    commanderId: string;
    storeId: string;
  }>();

  const [
    refetchNodesToInstall,
    {
      isFetching: isFetchingNodesToInstall,
      data: nodesToInstall,
      error: errorNodesToInstall,
    },
  ] = CommanderAirConnectAPI.useLazyGetNodesToInstallQuery();

  const [
    refetchNodesInstalled,
    {
      isFetching: isFetchingNodesInstalled,
      data: nodesInstalled,
      error: errorNodesInstalled,
    },
  ] = CommanderAirConnectAPI.useLazyGetNodesInstalledQuery();

  // Filter values with nodesInstalled
  useEffect(() => {
    formik.setValues(nodesInstalled?.map((node) => node.macAddress) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesInstalled]);

  const [
    installNodes,
    { isLoading: isInstallingNodes, error: errorInstallNodes },
  ] = CommanderAirConnectAPI.useInstallNewNodesMutation();
  const [loading, setLoading] = useState(false);

  const error = errorNodesToInstall || errorNodesInstalled || errorInstallNodes;

  const isFetching =
    isFetchingNodesToInstall ||
    isFetchingNodesInstalled ||
    isInstallingNodes ||
    loading;

  const refetch = async () => {
    try {
      setLoading(true);
      const airConnectNodeScanned = await refetchNodesToInstall(
        commanderId
      ).unwrap();
      await waitPromise(1000 * 2);
      const installedNodes = await refetchNodesInstalled(commanderId).unwrap();
      await waitPromise(1000 * 2);
      const nodesToInstall = [
        ...airConnectNodeScanned.map((node) => node.macAddress),
        ...installedNodes.map((node) => node.macAddress),
      ];
      await installNodes({
        commanderId,
        macAddresses: nodesToInstall,
      });
      await waitPromise(1000 * 10);
    } catch (e) {
      console.error('Error fetching nodes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      goToNextPage(values);
    },
  });

  const goToNextPage = (macAddresses?: string[]) => {
    navigate('../devices-setup', { state: { macAddresses } });
  };

  const onClickMacAddress = (macAddress: string) => {
    if (formik.values.includes(macAddress)) {
      formik.setValues(formik.values.filter((v) => v !== macAddress));
    } else {
      formik.setValues([...formik.values, macAddress]);
    }
  };

  const onClickBack = useBack('/');
  return (
    <Container maxWidth="xl">
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {parseErrorMessage(error)}
        </Alert>
      ) : null}
      <Box
        sx={(theme) => ({
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginLeft: 2,
          fill: theme.palette.text.primary,
        })}
      >
        <Stack direction={'row'} alignItems={'center'}>
          <AirConnectIcon height={'2em'} />
          <Typography
            variant="subtitle2"
            marginY={2}
            sx={{
              display: 'flex',
              marginLeft: 3,
              alignItems: 'center',
            }}
          >
            {t('initial_setup.scan_air_connect.node_found', {
              count: nodesInstalled?.length || 0,
            })}
            : {nodesInstalled?.length || 0}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          size="large"
          loading={isFetching}
          onClick={refetch}
        >
          <Stack
            direction={'row'}
            spacing={2}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <ScanAirConnectIcon height={'2em'} />
            <p>{t('initial_setup.scan_air_connect.action_rescan')}</p>
          </Stack>
        </Button>
      </Box>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {isFetchingNodesToInstall || isFetchingNodesInstalled || loading ? (
          <Box textAlign={'center'} mt={4}>
            <Stack spacing={2} alignItems={'center'}>
              <CircularProgress size={'4em'} />
              <Typography>
                {t('initial_setup.scan_air_connect.scan_loading')}
              </Typography>
            </Stack>
          </Box>
        ) : null}
        <Table
          sx={{
            '& th.header_store': {
              minWidth: 400,
            },
          }}
        >
          <caption></caption>

          <thead style={{ border: 'none' }}>
            <PaperButton component={'tr'}>
              <Typography
                component={'th'}
                color="primary"
                variant="subtitle2"
              ></Typography>
              <Typography
                component={'td'}
                variant="subtitle2"
                sx={{ textAlign: 'left!important' }}
              >
                {t('initial_setup.scan_air_connect.select_all')}
              </Typography>
              <Typography
                component={'td'}
                variant="subtitle2"
                sx={{ textAlign: 'left!important' }}
              >
                {t('initial_setup.scan_air_connect.wifi_signal')}
              </Typography>
            </PaperButton>
          </thead>
          <tbody>
            {nodesToInstall?.map((node) => {
              return (
                <PaperButton
                  component={'tr'}
                  key={node.macAddress}
                  elevation={undefined}
                  onClick={() => onClickMacAddress(node.macAddress)}
                >
                  <Stack
                    spacing={2}
                    alignItems={'center'}
                    flexDirection={'row'}
                    justifyContent={'center'}
                    gap={'1rem'}
                    padding={2}
                  >
                    <CircularProgress size={'2em'} />
                    {t('initial_setup.scan_air_connect.scanning_node')}
                  </Stack>
                  <td>
                    <Typography
                      variant="body1"
                      textAlign={'left'}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '110px 1fr',
                        alignItems: 'center',
                      }}
                    >
                      {t('initial_setup.scan_air_connect.node_found_one')}
                      <Tooltip title={node.macAddress}>
                        <Typography
                          variant="body2"
                          color={'primary'}
                          component={'span'}
                          ml={2}
                        >
                          <InfoIcon width={'1.5rem'} />
                        </Typography>
                      </Tooltip>
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      variant="body1"
                      textAlign={'left'}
                      component={'div'}
                      height={'2em'}
                      display={'flex'}
                      alignItems={'center'}
                      gap={'2em'}
                      marginLeft={2}
                    >
                      <GetWifiSignalIcon rssi={node.rssi} />
                    </Typography>
                  </td>
                </PaperButton>
              );
            })}

            {nodesInstalled?.map((node) => {
              return (
                <Paper
                  component={'tr'}
                  key={node.macAddress}
                  elevation={undefined}
                >
                  <td>{t('initial_setup.scan_air_connect.node_found')}</td>
                  <td>
                    <Typography
                      variant="body1"
                      textAlign={'left'}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '110px 1fr',
                      }}
                    >
                      {t(
                        'initial_setup.scan_air_connect.node_type_' +
                          node?.nodeType
                      )}
                      <Tooltip title={node.macAddress}>
                        <Typography
                          variant="body2"
                          color={'primary'}
                          component={'span'}
                          ml={2}
                        >
                          <InfoIcon width={'1.5rem'} />
                        </Typography>
                      </Tooltip>
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      variant="body1"
                      textAlign={'left'}
                      component={'div'}
                      height={'2em'}
                      display={'flex'}
                      alignItems={'center'}
                      gap={'2em'}
                      marginLeft={2}
                    >
                      <GetWifiSignalIcon rssi={node.rssi} />
                      {node.rssi}
                    </Typography>
                  </td>
                </Paper>
              );
            })}
          </tbody>
        </Table>

        <Grid container spacing={2} justifyContent={'space-between'} mt={2}>
          <Grid
            size={{
              xs: 12,
              sm: 2,
            }}
          >
            <BackButton onClick={onClickBack} />
          </Grid>
          <Grid
            display={'flex'}
            gap={2}
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              rounded
              fullWidth
              disabled={!formik.isValid || isFetching}
              loading={formik.isSubmitting}
              onClick={() => formik.handleSubmit()}
            >
              {t('button.next')}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
}

export default ScanAirConnect;
